import React from 'react';
import {
    Dialog,
    DialogContent,
    Button,
    Box,
    Typography,
    IconButton,
    Slide,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { PopupSetting } from '@/types';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface QuotePopupProps {
    open: boolean;
    onClose: () => void;
    settings: PopupSetting | null;
}

const QuotePopup: React.FC<QuotePopupProps> = ({ open, onClose, settings }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    const handleButtonClick = () => {
        onClose();
        const link = settings?.button_link || '/contact';
        if (link.startsWith('http')) {
            window.open(link, '_blank', 'noopener,noreferrer');
        } else {
            navigate(link);
        }
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            sx={{ maxWidth: '550px', mx: 'auto' }}
            PaperProps={{
                component: motion.div,
                initial: { scale: 0.92, opacity: 0 },
                animate: { scale: 1, opacity: 1 },
                exit: { scale: 0.92, opacity: 0 },
                transition: { duration: 0.28 },
                sx: {
                    borderRadius: isMobile ? 0 : '4px',
                    overflow: 'hidden',
                    background: 'rgba(255, 255, 255, 0.97)',
                    boxShadow: `0 8px 48px rgba(0,0,0,0.55)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                },
            }}
        >
            <Box sx={{ width: 'calc(100% - 24px)', position: 'relative', border: '1.5px solid rgba(0,0,0,0.7)', m: 1.5}}>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        zIndex: 10,
                        color: 'black',
                    }}
                >
                    <CloseIcon sx={{ fontSize: '1.1rem' }} />
                </IconButton>

                <DialogContent sx={{ py: 8, px: { xs: 3, sm: 6 }, textAlign: 'center' }}>
                    {/* Overline / label */}
                    <Typography
                        sx={{
                            display: 'block',
                            fontFamily: 'Butler, serif',
                            fontWeight: 500,
                            fontSize: { xs: '1.25em', sm: '1.75rem' },
                            textTransform: 'uppercase',
                            color: 'black',
                            mb: 2,
                        }}
                    >
                        {settings?.title ?? ''}
                    </Typography>

                    {/* Main headline */}
                    <Typography
                        sx={{
                            display: 'block',
                            fontFamily: 'Butler, serif',
                            fontWeight: 500,
                            fontSize: { xs: '1.25em', sm: '1.75rem' },
                            textTransform: 'none',
                            lineHeight: 1.2,
                            color: 'black',
                            textAlign: 'center',
                            m: '30px auto',
                            maxWidth: { xs: '100%', sm: '58%' },
                        }}
                    >
                        {settings?.description ?? ''}
                    </Typography>

                    {/* CTA Button */}
                    <Button
                        variant="outlined"
                        onClick={handleButtonClick}
                        sx={{
                            fontFamily: '"Butler", serif',
                            color: 'white',
                            borderColor: 'transparent',
                            borderWidth: '1.5px',
                            borderRadius: 0,
                            px: 4,
                            py: 1,
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            letterSpacing: '0.18em',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            '&:hover': {
                                backgroundColor: 'white',
                                color: 'rgba(0,0,0,0.7)',
                                borderColor: 'rgba(0,0,0,0.7)',
                            },
                        }}
                    >
                        {settings?.button_text ?? ''}
                    </Button>
                </DialogContent>
            </Box>
        </Dialog>
    );
};

export default QuotePopup;

